import React from "react";
import { Link } from "react-router-dom";
import { Clock, Eye, MessageSquare } from "lucide-react";
import { categoryName, timeAgoKannada } from "@/lib/api";

function shareCardToWhatsApp(e, article) {
  e.preventDefault();
  e.stopPropagation();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const shareUrl = `${BACKEND}/api/share/article/${article.id}`;
  const text = `📰 ${article.title}\n\n${shareUrl}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noreferrer");
}

export default function NewsCard({ article, variant = "default", index = 0 }) {
  const to = `/article/${article.id}`;
  if (variant === "hero") {
    return (
      <Link
        to={to}
        className="group relative block overflow-hidden rounded-sm border border-border bg-card card-lift fade-up"
        style={{ animationDelay: `${index * 60}ms` }}
        data-testid={`article-card-${article.id}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={article.image_url}
            alt=""
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
          <div className="absolute top-4 left-4 flex gap-2">
            {article.is_breaking && (
              <span className="bg-primary text-primary-foreground text-[10px] tracking-[0.2em] uppercase font-black px-2.5 py-1 rounded-sm">
                ಬ್ರೇಕಿಂಗ್
              </span>
            )}
            <span className="bg-background/90 text-foreground text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 rounded-sm">
              {categoryName(article.category)}
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
            <h2 className="font-kannada-heading text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl">
              {article.title}
            </h2>
            <p className="mt-3 text-sm sm:text-base opacity-90 max-w-2xl line-clamp-2">
              {article.summary}
            </p>
            <div className="mt-4 flex items-center gap-4 text-xs opacity-80">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {timeAgoKannada(article.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" /> {article.views || 0}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={to}
        className="group flex gap-3 border-b border-border pb-3 fade-up"
        style={{ animationDelay: `${index * 40}ms` }}
        data-testid={`article-card-${article.id}`}
      >
        <div className="w-24 h-20 shrink-0 overflow-hidden rounded-sm">
          <img
            src={article.image_url}
            alt=""
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-primary">
            {categoryName(article.category)}
          </div>
          <div className="font-kannada-heading text-base leading-snug mt-1 line-clamp-2 group-hover:underline underline-offset-4">
            {article.title}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {timeAgoKannada(article.created_at)}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group block bg-card border border-border rounded-sm overflow-hidden card-lift fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`article-card-${article.id}`}
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image_url}
          alt=""
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold">
          <span className="text-primary">{categoryName(article.category)}</span>
          <span className="text-muted-foreground">· {timeAgoKannada(article.created_at)}</span>
          <button
            onClick={(e) => shareCardToWhatsApp(e, article)}
            className="ml-auto flex items-center gap-1 text-[#128C7E] hover:text-[#25D366] transition-colors"
            aria-label="Share on WhatsApp"
            data-testid={`card-whatsapp-share-${article.id}`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
        </div>
        <h3 className="font-kannada-heading text-xl leading-tight mt-2 line-clamp-3 group-hover:text-primary transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2 font-kannada-body">
          {article.summary}
        </p>
      </div>
    </Link>
  );
}
